<template>
  <div class="job-progress">
    <template v-if="!hideProgress">
      <el-steps :active="activeStep" align-center class="mt10">
        <el-step
          v-for="t in tasks"
          :key="t.id"
          :title="agentLabel(t.agent)"
          :description="statusLabel(t.status)"
          :status="stepStatus(t)"
          :icon="stepIcon(t)"
        />
      </el-steps>

      <el-alert
        v-if="job && job.status === 'failed'"
        type="error"
        :title="job.error || '任務失敗'"
        :closable="false"
        class="mt10"
      />
      <el-alert
        v-else-if="job && job.status === 'success'"
        type="success"
        title="任務完成"
        :closable="false"
        class="mt10"
      />
      <el-alert
        v-else-if="job && job.status === 'cancelled'"
        type="warning"
        title="任務已取消"
        :closable="false"
        class="mt10"
      />
      <el-alert
        v-else-if="job && (job.status === 'running' || job.status === 'queued')"
        type="info"
        title="任務執行中…"
        :closable="false"
        class="mt10"
      />
    </template>
    <!-- 任务完成反馈（帮助系统记住你的偏好） -->
    <div v-if="job && job.status === 'success'" class="mt10 feedback-bar">
      <template v-if="feedbackGiven">
        <span class="feedback-result" :class="feedbackGood ? 'good' : 'bad'">
          <i :class="feedbackGood ? 'el-icon-success' : 'el-icon-error'"></i>
          已回饋：{{ feedbackGood ? '滿意' : '不滿意' }}
        </span>
      </template>
      <template v-else>
        <span class="muted" style="font-size:12px">這個結果滿意嗎？回饋可讓系統越用越懂你的風格</span>
        <el-button size="mini" type="success" icon="el-icon-check" :loading="feedbackLoading" @click="feedbackJob(true)">滿意</el-button>
        <el-button size="mini" type="danger" icon="el-icon-close" :loading="feedbackLoading" @click="feedbackJob(false)">不滿意</el-button>
      </template>
    </div>
    <!-- 失败子任务的具体错误（独立于上面的 v-if 链） -->
    <el-alert
      v-for="t in failedTasks"
      :key="'fail-' + t.id"
      type="error"
      :title="'【' + agentLabel(t.agent) + '】' + taskError(t)"
      :closable="false"
      class="mt10"
    />

    <template v-if="showMaterials">
      <div v-if="materials.length" class="section-chip">
        <span class="chip-dot"></span>產出素材
        <span class="chip-stat">
          （首次 {{ firstCount }} 個<template v-if="redoCount"> · 二次修改 {{ redoCount }} 個</template> · 共 {{ materials.length }} 個）
        </span>
      </div>
      <el-row v-if="materials.length" :gutter="12">
        <el-col v-for="m in materials" :key="m.id" :span="6" class="mt10">
          <MaterialCard :material="m" @upscaled="refresh" />
        </el-col>
      </el-row>
    </template>
  </div>
</template>

<script>
import { fetchJobDetail } from '../api/jobs'
import { subscribeJobEvents } from '../utils/sse'
import { feedbackJob as apiFeedback } from '../api/prefs'
import MaterialCard from './MaterialCard.vue'

export default {
  name: 'JobProgress',
  components: { MaterialCard },
  props: {
    jobId: { type: [Number, String], required: true },
    // 是否在组件内展示产出素材（工作台用 false，由父级用 ResultGallery 展示）
    showMaterials: { type: Boolean, default: true },
    // 隐藏步骤条/提示（工作台用 true：结果区改为「图片逐个出现」）
    hideProgress: { type: Boolean, default: false }
  },
  data() {
    return {
      job: null,
      tasks: [],
      materials: [],
      redoChildren: [],
      es: null,
      timer: null,
      feedbackLoading: false,
      feedbackGiven: false,
      feedbackGood: false
    }
  },
  computed: {
    activeStep() {
      // 第一个「未完成」任务的位置；全部完成才返回 length
      for (let i = 0; i < this.tasks.length; i++) {
        if (this.tasks[i].status === 'success') continue
        return i
      }
      return this.tasks.length
    },
    failedTasks() {
      return this.tasks.filter(t => t.status === 'failed')
    },
    redoCount() {
      return (this.materials || []).filter(m => m.redo_from_id).length
    },
    firstCount() {
      return (this.materials || []).length - this.redoCount
    }
  },
  async created() {
    await this.refresh()
    this.es = subscribeJobEvents(this.jobId, {
      onJobUpdate: d => {
        this.job = { ...(this.job || {}), status: d.status }
        // 终态后关闭 SSE，避免连接泄漏与无谓重连
        if (['success', 'failed', 'cancelled'].indexOf(d.status) >= 0 && this.es) {
          this.es.close()
        }
        // 通知父级：任务到达终态（结束 loading 槽）
        if (['success', 'failed', 'cancelled'].indexOf(d.status) >= 0) {
          this.$emit('job-status', d.status)
        }
      },
      onTaskUpdate: d => {
        const t = this.tasks.find(x => String(x.id) === String(d.taskId))
        if (t) t.status = d.status
      },
      onMaterialAdded: d => {
        ;(d.files || []).forEach(f => {
          this.materials.push({ id: d.taskId + '_' + this.materials.length, type: d.type, file_path: f })
        })
        this.$emit('material-added', d)
        // 逐张生成时立即回推给父级，让图片「生成一张显示一张」
        this.$emit('materials-updated', this.materials)
      }
    })
    // SSE 兜底：轮询刷新（终态后停止），保证结果不因事件丢失而不显示
    this.timer = setInterval(() => {
      if (this.job && ['success', 'failed', 'cancelled'].indexOf(this.job.status) >= 0) {
        clearInterval(this.timer)
        return
      }
      this.refresh()
    }, 5000)
  },
  methods: {
    /** agent 标识 → 中文描述 */
    agentLabel(agent) {
      const map = {
        copywriter: '產生文案',
        main_image: '產生主圖',
        detail_image: '產生詳情圖',
        sku_image: '產生SKU 圖',
        promotion_image: '產生推廣圖',
        scene_image: '產生場景圖',
        video: '產生影片',
        quality: '最終質檢'
      }
      return map[agent] || agent
    },
    /** 任务状态 → 中文描述 */
    statusLabel(status) {
      const map = {
        pending: '等待中',
        queued: '排隊中',
        running: '執行中',
        success: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      }
      return map[status] || status
    },
    /** 二次修改子任务的修改意见 */
    redoFeedback(c) {
      const ms = c.materials || []
      for (const m of ms) {
        if (m.feedback) return m.feedback
      }
      return (c.job && c.job.error) || '—'
    },
    /** 步骤状态（覆盖 el-steps 的默认流转） */
    stepStatus(t) {
      if (t.status === 'success') return 'success'
      if (t.status === 'failed') return 'error'
      return ''
    },
    /** 步骤图标：执行中显示转动 loading */
    stepIcon(t) {
      if (t.status === 'running') return 'el-icon-loading'
      return ''
    },
    /** 从任务的 result_json 提取错误信息 */
    taskError(t) {
      try {
        const rj = JSON.parse(t.result_json || '{}')
        return rj.error || t.result_json || '未知錯誤'
      } catch (e) {
        return t.result_json || '未知錯誤'
      }
    },
    async feedbackJob(good) {
      if (this.feedbackLoading) return
      this.feedbackLoading = true
      try {
        const msg = await apiFeedback(this.jobId, good)
        this.feedbackGood = !!good
        this.feedbackGiven = true
        this.$message && this.$message.success(msg || ('已回饋：' + (good ? '滿意' : '不滿意')))
      } catch (e) {
        this.$message && this.$message.error('回饋失敗：' + (e.message || e))
      } finally {
        this.feedbackLoading = false
      }
    },
    async refresh() {
      try {
        const data = await fetchJobDetail(this.jobId)
        this.job = data.job
        this.tasks = data.tasks || []
        this.materials = data.materials || []
        this.redoChildren = data.redo_children || []
        this.$emit('materials-updated', this.materials)
        // 恢复已提交过的反馈（满意/不满意），重新打开详情不再要求重选
        const fb = this.job && this.job.feedback
        if (fb !== null && fb !== undefined && fb !== '') {
          this.feedbackGiven = true
          this.feedbackGood = (fb === 1 || fb === '1' || fb === true)
        }
        // 轮询兜底：拿到终态也要通知父级，否则父级结果区的 loading 槽不会消失
        const st = this.job && this.job.status
        if (['success', 'failed', 'cancelled'].indexOf(st) >= 0) {
          this.$emit('job-status', st)
        }
      } catch (e) {
        // 任务可能刚创建，容忍一次失败
      }
    }
  },
  beforeDestroy() {
    if (this.es) this.es.close()
    if (this.timer) clearInterval(this.timer)
  }
}
</script>

<style scoped>
/* 反馈条：不加底色/边框，直接贴在生成结果下方（原先的灰底已去掉） */
.feedback-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.feedback-result {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.feedback-result.good {
  color: #67c23a;
}
.feedback-result.bad {
  color: #f56c6c;
}

/* 任务详情在深色主题下的统一协调配色（覆盖 Element 默认 alert 的亮色） */
.section-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 13px;
  color: var(--c-primary);
  background: rgba(30, 64, 175, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.35);
}
.chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--c-primary);
  box-shadow: 0 0 6px rgba(59, 130, 246, 0.8);
}
.chip-stat {
  font-size: 12px;
  color: #94a3c8;
}
.job-progress ::v-deep .el-alert {
  border-radius: 6px;
  border: 1px solid rgba(59, 130, 246, 0.25) !important;
  background: var(--c-surface-2) !important;
}
.job-progress ::v-deep .el-alert__title {
  color: var(--c-text-2) !important;
  font-size: 13px;
  font-weight: 400;
}
.job-progress ::v-deep .el-alert--info {
  border-color: rgba(59, 130, 246, 0.35) !important;
  background: rgba(30, 64, 175, 0.10) !important;
}
.job-progress ::v-deep .el-alert--info .el-alert__title,
.job-progress ::v-deep .el-alert--info .el-alert__icon {
  color: var(--c-primary) !important;
}
/* 「任务完成」不再用绿色：绿只留给「满意」评价按钮，
   避免同一屏里两处绿打架；这里退成中性提示。 */
.job-progress ::v-deep .el-alert--success {
  border-color: var(--c-split) !important;
  background: var(--c-surface-2) !important;
}
.job-progress ::v-deep .el-alert--success .el-alert__title,
.job-progress ::v-deep .el-alert--success .el-alert__icon {
  color: var(--c-text-3) !important;
}
.job-progress ::v-deep .el-alert--warning {
  border-color: rgba(230, 162, 60, 0.4) !important;
  background: rgba(230, 162, 60, 0.10) !important;
}
.job-progress ::v-deep .el-alert--warning .el-alert__title,
.job-progress ::v-deep .el-alert--warning .el-alert__icon {
  color: #a8701a !important;
}
.job-progress ::v-deep .el-alert--error {
  border-color: rgba(245, 108, 108, 0.4) !important;
  background: rgba(245, 108, 108, 0.10) !important;
}
.job-progress ::v-deep .el-alert--error .el-alert__title,
.job-progress ::v-deep .el-alert--error .el-alert__icon {
  color: #cf3f3f !important;
}
</style>
